import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MainContainer } from '../MainContainer';
import {
    getApplicationChangeProfile,
    getApplicationChangeProfileById,
    searchApplicationChangeProfile,
} from '../../actions';
import RenderDetailsModal from './RenderDetailsModal';

const getApplicationId = value => (
    typeof value === 'string' ? value : value?._id || value?.id || ''
);

const getVariantLabel = variant => {
    switch (variant) {
        case 'personal':
            return 'Личные данные';
        case 'contact':
            return 'Контакты';
        case 'login':
            return 'Email';
        case 'password':
            return 'Пароль';
        default:
            return 'Профиль';
    }
};

const getSourceLabel = source => {
    switch (source) {
        case 'mobile':
            return 'MOBILE';
        case 'admin':
            return 'ADMIN';
        case 'system':
            return 'Система';
        case 'legacy':
            return 'Старый кабинет';
        default:
            return 'WEB';
    }
};

const getFullName = user => (
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.email ||
    'Пользователь'
);

const formatDateTime = value => {
    if (!value) return '—';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getChangeSummary = item => {
    if (Array.isArray(item?.changes) && item.changes.length) {
        return item.changes
            .map(change => change?.label || change?.field)
            .filter(Boolean)
            .join(', ');
    }

    return item?.audit
        ? 'Изменение профиля'
        : 'Legacy-запись';
};

const ApplicationsChangeProfile = () => {
    const {
        applicationChangeProfileObj,
        applicationsChangeProfile,
        loading,
    } = useSelector(state => state.applicationChangeProfile);

    const dispatch = useDispatch();

    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [sortItems] = useState('updatedAtMinus');

    useEffect(() => {
        dispatch(getApplicationChangeProfile('all', sortItems));
    }, [dispatch, sortItems]);

    const items = useMemo(
        () => Array.isArray(applicationsChangeProfile) ? applicationsChangeProfile : [],
        [applicationsChangeProfile]
    );

    const handleClose = () => {
        setShowDetailsModal(false);
        setSelectedItemId(null);
    };

    const showModal = itemOrId => {
        const id = getApplicationId(itemOrId);
        if (!id) return;

        setSelectedItemId(id);
        setShowDetailsModal(true);
        dispatch(getApplicationChangeProfileById(id));
    };

    return (
        <MainContainer
            backButtonProduct
            type
            itemsType="application"
            sortItems={sortItems}
            backHref
            search={searchApplicationChangeProfile}
            get={getApplicationChangeProfile}
            title="История изменений профиля"
        >
            <div className="applicationChangeProfile">
                <div className="applicationChangeProfile__pageIntro">
                    <div>
                        <div className="applicationChangeProfile__eyebrow">Аудит профиля</div>
                        <div className="applicationChangeProfile__pageTitle">
                            История изменений пользователей
                        </div>
                        <div className="applicationChangeProfile__pageDescription">
                            Здесь только журнал. Обычные изменения профиля применяются пользователем самостоятельно,
                            а email и пароль подтверждаются защищённым потоком.
                        </div>
                    </div>

                    <div className="applicationChangeProfile__counter">
                        {items.length} записей
                    </div>
                </div>

                {loading && !items.length ? (
                    <div className="applicationChangeProfile__emptyState">
                        Загружаем историю…
                    </div>
                ) : items.length ? (
                    <div className="applicationChangeProfile__historyTableWrap">
                        <table className="applicationChangeProfile__historyTable">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Пользователь</th>
                                    <th>Что изменено</th>
                                    <th>Источник</th>
                                    <th>Кто изменил</th>
                                    <th>Время</th>
                                </tr>
                            </thead>

                            <tbody>
                                {items.map(item => (
                                    <tr
                                        key={item._id}
                                        onClick={() => showModal(item)}
                                    >
                                        <td>
                                            <strong>{item.applicationNumber || '—'}</strong>
                                        </td>
                                        <td>
                                            <div className="applicationChangeProfile__userCell">
                                                <strong>{getFullName(item.userObj)}</strong>
                                                <span>{item.userObj?.email || '—'}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="applicationChangeProfile__changeCell">
                                                <strong>{getVariantLabel(item.variant)}</strong>
                                                <span>{getChangeSummary(item)}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="applicationChangeProfile__sourceBadge">
                                                {getSourceLabel(item.audit ? item.source : "legacy")}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="applicationChangeProfile__actorCell">
                                                <strong>
                                                    {item.actorId
                                                        ? getFullName(item.actorObj || item.userObj)
                                                        : 'Legacy'}
                                                </strong>
                                                <span>
                                                    {item.actorId
                                                        ? `${item.actorRole || 'user'} · ${item.actorObj?.email || String(item.actorId)}`
                                                        : 'Старая запись'}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            {formatDateTime(item.appliedAt || item.createdAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="applicationChangeProfile__emptyState">
                        История изменений пока пустая
                    </div>
                )}

                <RenderDetailsModal
                    item={applicationChangeProfileObj}
                    loading={loading}
                    selectedItemId={selectedItemId}
                    showDetailsModal={showDetailsModal}
                    handleClose={handleClose}
                />
            </div>
        </MainContainer>
    );
};

export default ApplicationsChangeProfile;
